// The page's one entry: `pnpm --filter @canvas/app dev` serves index.html, which loads this
// module. Not part of the library; nothing imports it. With `VITE_SITE_PAGES=1` at build time it
// starts the site's pages (src/site/start-site.tsx) from a dynamic import; without it, in every
// build (development, the scenario harness, the golden suite, and the deployable `build:site`,
// whose `VITE_SITE_BUILD=1` only locks the endpoints), it starts the playground
// (start-playground.tsx) exactly as before and carries none of the site: the condition is a
// build-time constant, so the build drops the branch it does not take. Nothing in the repository
// sets `VITE_SITE_PAGES` yet; the deploy turns it on once `/api` reaches the boards service.
// First, so no schema parses before zod is told not to probe for eval (VIV-723).
import './schema-without-eval.js';

import { createRoot } from 'react-dom/client';

import { isCanvasHarnessEnabled } from '../harness/use-shell-harness.js';

import { sessionStorageOf } from './playground-board.js';
import { startPlayground } from './start-playground.js';

const container = document.getElementById('root');
if (container === null) throw new Error('index.html has no #root element to mount the shell in.');
const root = createRoot(container);

const isSitePages = import.meta.env.VITE_SITE_PAGES === '1';
// The site's pages only in a deployable build without the test harness (the deploy sets both).
if (
  isSitePages &&
  (import.meta.env.VITE_SITE_BUILD !== '1' || import.meta.env.VITE_CANVAS_HARNESS === '1')
) {
  throw new Error('VITE_SITE_PAGES needs VITE_SITE_BUILD=1 and no VITE_CANVAS_HARNESS.');
}

if (isSitePages) {
  import('../site/start-site.js')
    .then(({ startSite }) => {
      startSite({
        env: {
          relayUrl: import.meta.env.VITE_RELAY_URL,
          assetsUrl: import.meta.env.VITE_ASSETS_URL,
        },
        host: window,
        render: (element) => {
          root.render(element);
        },
      });
    })
    .catch((error: unknown) => {
      reportError(error);
    });
} else {
  startPlayground({
    search: window.location.search,
    isHarness: isCanvasHarnessEnabled,
    env: {
      relayUrl: import.meta.env.VITE_RELAY_URL,
      tokenUrl: import.meta.env.VITE_TOKEN_URL,
      assetsUrl: import.meta.env.VITE_ASSETS_URL,
      isSiteBuild: import.meta.env.VITE_SITE_BUILD,
    },
    storage: () => sessionStorageOf(window),
    random: Math.random,
    render: (element) => {
      root.render(element);
    },
  }).catch((error: unknown) => {
    reportError(error);
  });
}
